import { Button, Container, Heading, Text, toast, usePrompt } from "@medusajs/ui"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { dateTime, money } from "../lib/format"
import { t } from "../lib/i18n"
import { errorMessage, hqApi, type OrderDetail, type OrderEmail } from "../lib/medusa"
import { newRequestId } from "../lib/payment-links"

/** What each email is, as the owners know it. */
function emailName(email: OrderEmail): string {
  switch (email.type) {
    case "confirmation":
      return t("Order confirmation")
    case "shipped":
      return t("Sent: with the tracking link")
    case "delivered":
      return t("Handed over")
    case "canceled":
      return t("Order cancelled")
    case "refunded":
      return t("Refund of {amount}", { amount: money(email.amount ?? 0) })
    case "returned":
      return t("Return received")
    case "exchange_sent":
      return t("Exchange sent")
  }
}

/**
 * The emails the customer has had, each with "Send again" (NEXT-STEPS B9): for a customer who says
 * nothing arrived. It goes to the customer only, under its own key, and is recorded here.
 */
export function OrderEmails({ order }: { order: OrderDetail }) {
  const queryClient = useQueryClient()
  const prompt = usePrompt()
  const emails = order.emails ?? []
  const resends = order.email_resends ?? []

  const resend = useMutation({
    mutationFn: (email: OrderEmail) => hqApi.resendOrderEmail(order.id, { type: email.type, id: email.id, request_id: newRequestId() }),
    onSuccess: (body) => {
      queryClient.setQueryData(["hq", "order", order.id], body.order)
      toast.success(body.resend.mode === "live" && body.resend.to ? t("Sent again to {email}.", { email: body.resend.to }) : t("Sent again, to the shop inbox as a preview."))
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  if (!emails.length) return null

  const ask = async (email: OrderEmail) => {
    if (!order.email) return toast.error(t("This order has no email address."))
    const ok = await prompt({
      title: t("Send «{name}» again?", { name: emailName(email) }),
      description: t("It goes to {email}, written as the first time. The shop's own copy is not sent again.", { email: order.email }),
      confirmText: t("Send again"),
      cancelText: t("Cancel"),
    })
    if (ok) resend.mutate(email)
  }

  return (
    <Container className="flex flex-col gap-y-3">
      <div className="flex flex-col gap-y-0.5">
        <Heading level="h2">{t("Emails to the customer")}</Heading>
        <Text size="xsmall" className="text-ui-fg-subtle">
          {order.email ? t("To {email}. If something did not arrive, send it again.", { email: order.email }) : t("This order has no email address.")}
        </Text>
      </div>
      <ul className="divide-ui-border-base flex flex-col divide-y">
        {emails.map((email) => {
          const again = resends.filter((entry) => entry.type === email.type && entry.id === email.id)
          const last = again[again.length - 1]
          return (
            <li key={`${email.type}:${email.id}`} className="flex items-center justify-between gap-x-3 py-2">
              <div className="flex min-w-0 flex-col">
                <Text size="small">{emailName(email)}</Text>
                <Text size="xsmall" className="text-ui-fg-muted">
                  {email.at ? dateTime(email.at) : ""}
                  {last ? `${email.at ? " · " : ""}${t.n(again.length, "sent again {n} time, last {date}", "sent again {n} times, last {date}", { date: dateTime(last.at) })}` : ""}
                </Text>
              </div>
              <Button
                variant="secondary"
                size="small"
                className="shrink-0"
                disabled={!order.email || resend.isPending}
                isLoading={resend.isPending && resend.variables?.type === email.type && resend.variables?.id === email.id}
                onClick={() => void ask(email)}
              >
                {t("Send again")}
              </Button>
            </li>
          )
        })}
      </ul>
    </Container>
  )
}
